import {
  FormValues,
  IJSONSchema,
  IJSONSchemaProperty,
  SchemaType
} from './integration-test-modal.types';

const DEFINITIONS_PREFIX = '#/definitions/';
const CAMEL_CASE_REGEX = /([A-Z])/g;
const FIRST_CHAR_REGEX = /^./;
const STANDALONE_ID_REGEX = /\bId\b/g;

export const schemaUtils = {
  resolveRef: (
    ref: string,
    definitions: Record<string, IJSONSchemaProperty> | undefined
  ): IJSONSchemaProperty | undefined => {
    if (!definitions || !ref.startsWith(DEFINITIONS_PREFIX)) {
      return undefined;
    }
    const definitionName = ref.replace(DEFINITIONS_PREFIX, '');
    return definitions[definitionName];
  },

  formatFieldLabel: (name: string, title?: string): string => {
    if (title) return title;
    return name
      .replace(CAMEL_CASE_REGEX, ' $1')
      .replace(FIRST_CHAR_REGEX, (str) => str.toUpperCase())
      .trim()
      .replace(STANDALONE_ID_REGEX, 'ID');
  }
};

const DEFAULT_VALUE_HANDLERS: Record<
  SchemaType,
  (
    property: IJSONSchemaProperty,
    definitions?: Record<string, IJSONSchemaProperty>,
    buildForPropertiesFn?: typeof populateDefaultValuesFromProperties
  ) => unknown
> = {
  [SchemaType.BOOLEAN]: () => false,
  [SchemaType.NUMBER]: () => 0,
  [SchemaType.INTEGER]: () => 0,
  [SchemaType.STRING]: () => '',
  [SchemaType.ARRAY]: () => [],
  [SchemaType.OBJECT]: (property, definitions, buildForPropertiesFn) => {
    if (property.properties && buildForPropertiesFn) {
      const nestedObject: FormValues = {};
      buildForPropertiesFn(property.properties, nestedObject, definitions);
      return nestedObject;
    }
    return {};
  }
};

const populateDefaultValuesFromProperties = (
  properties: Record<string, IJSONSchemaProperty>,
  targetObject: FormValues,
  definitions?: Record<string, IJSONSchemaProperty>
): void => {
  Object.entries(properties).forEach(([propertyName, property]) => {
    let resolvedProperty = property;

    if (property.$ref) {
      const refProperty = schemaUtils.resolveRef(property.$ref, definitions);
      if (refProperty) {
        resolvedProperty = refProperty;
      }
    }

    if (resolvedProperty.default !== undefined) {
      targetObject[propertyName] = resolvedProperty.default;
      return;
    }

    if (resolvedProperty.enum && resolvedProperty.enum.length > 0) {
      targetObject[propertyName] = resolvedProperty.enum[0];
      return;
    }

    if (
      resolvedProperty.type &&
      DEFAULT_VALUE_HANDLERS[resolvedProperty.type]
    ) {
      targetObject[propertyName] = DEFAULT_VALUE_HANDLERS[
        resolvedProperty.type
      ](resolvedProperty, definitions, populateDefaultValuesFromProperties);
    } else {
      targetObject[propertyName] = {};
    }
  });
};

const isMergeableObject = (value: unknown): boolean => {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
};

const deepMergeFormValues = (
  target: FormValues,
  source: FormValues
): FormValues => {
  const result = { ...target };

  Object.keys(source).forEach((key) => {
    const sourceValue = source[key];
    const targetValue = target[key];

    if (isMergeableObject(sourceValue) && isMergeableObject(targetValue)) {
      result[key] = deepMergeFormValues(
        targetValue as FormValues,
        sourceValue as FormValues
      );
    } else if (sourceValue !== undefined) {
      result[key] = sourceValue;
    }
  });

  return result;
};

export const isValueEmpty = (value: unknown): boolean => {
  if (value === undefined) {
    return true;
  }
  if (typeof value === 'string' && value.trim() === '') {
    return true;
  }
  return false;
};

const recursivelyRemoveEmptyValues = (obj: FormValues): FormValues => {
  const result: FormValues = {};

  Object.entries(obj).forEach(([key, value]) => {
    if (value === null) {
      result[key] = null;
      return;
    }

    if (isValueEmpty(value)) {
      return;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        result[key] = value;
      }
      return;
    }

    if (typeof value === 'object') {
      const cleanedNested = recursivelyRemoveEmptyValues(value as FormValues);
      if (Object.keys(cleanedNested).length > 0) {
        result[key] = cleanedNested;
      }
      return;
    }

    result[key] = value;
  });

  return result;
};

const updateNestedValueInPath = (
  obj: FormValues,
  path: string,
  value: unknown
): FormValues => {
  const keys = path.split('.');
  const result = { ...obj };

  if (keys.length === 1) {
    result[keys[0]] = value;
    return result;
  }

  let current: FormValues = result;
  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (typeof current[key] !== 'object' || current[key] === null) {
      current[key] = {};
    } else {
      current[key] = { ...(current[key] as FormValues) };
    }
    current = current[key] as FormValues;
  }

  current[keys[keys.length - 1]] = value;
  return result;
};

interface IValidationResult {
  isValid: boolean;
  missingFields: string[];
}

export const formValueUtils = {
  buildDefaultValuesFromSchema: (
    schema: IJSONSchema,
    definitions?: Record<string, IJSONSchemaProperty>
  ): FormValues => {
    const defaultValues: FormValues = {};
    populateDefaultValuesFromProperties(
      schema.properties,
      defaultValues,
      definitions
    );
    return defaultValues;
  },

  setNestedValue: updateNestedValueInPath,

  removeEmptyValues: recursivelyRemoveEmptyValues,

  deepMerge: deepMergeFormValues,

  validateRequiredFields: (
    requiredFieldNames: string[] | undefined,
    formValues: FormValues,
    schemaProperties: Record<string, IJSONSchemaProperty>,
    definitions?: Record<string, IJSONSchemaProperty>
  ): IValidationResult => {
    if (!requiredFieldNames || requiredFieldNames.length === 0) {
      return { isValid: true, missingFields: [] };
    }

    const missingFields: string[] = [];

    const checkRequiredField = (
      fieldName: string,
      fieldValue: unknown,
      property: IJSONSchemaProperty,
      parentLabel?: string
    ): void => {
      if (isValueEmpty(fieldValue)) {
        const label = schemaUtils.formatFieldLabel(fieldName, property?.title);
        const fullLabel = parentLabel ? `${parentLabel} > ${label}` : label;
        missingFields.push(fullLabel);
        return;
      }

      if (fieldValue === null) {
        return;
      }

      if (property.type === SchemaType.OBJECT && property.properties && property.required) {
        const objectValue = fieldValue as FormValues;
        const currentLabel = schemaUtils.formatFieldLabel(fieldName, property?.title);
        const fullLabel = parentLabel ? `${parentLabel} > ${currentLabel}` : currentLabel;

        property.required.forEach((nestedFieldName) => {
          const nestedProperty = property.properties![nestedFieldName];
          const nestedFieldValue = objectValue[nestedFieldName];
          if (nestedProperty) {
            checkRequiredField(nestedFieldName, nestedFieldValue, nestedProperty, fullLabel);
          }
        });
      }

      if (property.$ref && definitions) {
        const resolvedProperty = schemaUtils.resolveRef(property.$ref, definitions);
        if (resolvedProperty?.type === SchemaType.OBJECT && resolvedProperty.properties && resolvedProperty.required) {
          const objectValue = fieldValue as FormValues;
          const currentLabel = schemaUtils.formatFieldLabel(fieldName, property?.title || resolvedProperty?.title);
          const fullLabel = parentLabel ? `${parentLabel} > ${currentLabel}` : currentLabel;

          resolvedProperty.required.forEach((nestedFieldName) => {
            const nestedProperty = resolvedProperty.properties![nestedFieldName];
            const nestedFieldValue = objectValue[nestedFieldName];
            if (nestedProperty) {
              checkRequiredField(nestedFieldName, nestedFieldValue, nestedProperty, fullLabel);
            }
          });
        }
      }
    };

    requiredFieldNames.forEach((fieldName) => {
      const fieldValue = formValues[fieldName];
      const property = schemaProperties[fieldName];
      if (property) {
        checkRequiredField(fieldName, fieldValue, property);
      }
    });

    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  }
};

const HTTP_STATUS_SUCCESS_MIN = 200;
const HTTP_STATUS_SUCCESS_MAX = 300;

const HTTP_STATUS_TEXT_MAP: Record<number, string> = {
  400: 'Bad Request',
  401: 'Unauthorized',
  403: 'Forbidden',
  404: 'Not Found',
  500: 'Internal Server Error',
  502: 'Bad Gateway',
  503: 'Service Unavailable',
  504: 'Gateway Timeout'
};

export const httpStatusUtils = {
  getStatusText: (statusCode: number, success: boolean): string => {
    if (
      success &&
      statusCode >= HTTP_STATUS_SUCCESS_MIN &&
      statusCode < HTTP_STATUS_SUCCESS_MAX
    ) {
      return 'OK';
    }
    return HTTP_STATUS_TEXT_MAP[statusCode] ?? (success ? 'Success' : 'Error');
  }
};
